import { convertHtmlToText } from '@/helpers/convertHtmlToText'

const OG_LOCALE_MAP = {
  tr: 'tr_TR',
  en: 'en_US',
  de: 'de_DE',
  kg: 'ky_KG',
  kz: 'kk_KZ',
}

/**
 * Meta açıklamaları için güvenli uzunluk (yaklaşık 155–160 karakter).
 */
export function truncateMetaText(text, max = 160) {
  if (text == null || text === '') return ''
  const s = String(text).trim()
  if (!s) return ''
  if (s.length <= max) return s
  return `${s.slice(0, max - 1).trimEnd()}…`
}

/**
 * HTML içeren metinleri meta için düz metne çevirir.
 */
export function metaPlainText(raw) {
  if (raw == null || raw === '') return ''
  return truncateMetaText(convertHtmlToText(String(raw)), 160)
}

/**
 * Görseller için mutlak URL (Open Graph / Twitter).
 */
export function absoluteAssetUrl(vm, url) {
  if (url == null || url === '') {
    const c = vm.$store.state.settings.contents[vm.$i18n.locale]
    url = c?.seo?.home?.image
  }
  if (url == null || url === '') return ''
  const u = String(url).trim()
  if (/^https?:\/\//i.test(u)) return u
  const base = (vm.$store.state.settings.website || '').replace(/\/$/, '')
  if (!base) return u
  return base + (u.startsWith('/') ? u : `/${u}`)
}

function resolveDescription(vm, description) {
  if (description != null && String(description).trim() !== '') {
    return metaPlainText(description)
  }
  const locale = vm.$i18n.locale
  const contents = vm.$store.state.settings.contents[locale] || {}
  const seoHome = contents.seo?.home || {}
  if (seoHome.description)
    return truncateMetaText(convertHtmlToText(seoHome.description), 160)
  return metaPlainText(contents.home?.banner?.overview || '')
}

function resolveCanonical(vm, canonicalHref) {
  if (canonicalHref) return String(canonicalHref).trim()
  const base = (vm.$store.state.settings.website || '').replace(/\/$/, '')
  const path = vm.$route?.path || '/'
  const p = path.startsWith('/') ? path : `/${path}`
  return `${base}${p}`
}

/**
 * Standart sayfa head parçaları: title, meta[], link[]
 *
 * @param {import('vue').default} vm
 * @param {object} opts
 * @param {string} opts.title — document + og:title
 * @param {string} [opts.description] — yoksa seo.home veya banner özeti
 * @param {string} [opts.image] — yoksa seo.home.image
 * @param {string} [opts.canonicalHref] — tam kanonik URL; yoksa website + route.path
 * @param {string} [opts.openGraphUrl] — og:url; yoksa canonical ile aynı
 * @param {string} [opts.ogType] — varsayılan 'website'
 * @param {string} [opts.twitterCard] — varsayılan 'summary_large_image'
 * @param {boolean} [opts.noindex]
 */
export function buildPageHead(vm, opts = {}) {
  const {
    title,
    description,
    image,
    canonicalHref,
    openGraphUrl,
    ogType = 'website',
    twitterCard = 'summary_large_image',
    noindex = false,
  } = opts

  const siteTitle = vm.$store.state.settings.title
  const locale = vm.$i18n.locale
  const pageTitle = title || siteTitle

  const desc = resolveDescription(vm, description)
  const img = absoluteAssetUrl(vm, image)
  const canonical = resolveCanonical(vm, canonicalHref, openGraphUrl)
  const ogUrl = openGraphUrl ? String(openGraphUrl).trim() : canonical
  const ogLocale = OG_LOCALE_MAP[locale] || 'en_US'

  const meta = [
    {
      hid: 'robots',
      name: 'robots',
      content: noindex ? 'noindex, nofollow' : 'index, follow',
    },
    { hid: 'description', name: 'description', content: desc },
    { hid: 'author', name: 'author', content: 'Heocademy' },
    { hid: 'og:type', property: 'og:type', content: ogType },
    { hid: 'og:site_name', property: 'og:site_name', content: siteTitle },
    { hid: 'og:title', property: 'og:title', content: pageTitle },
    { hid: 'og:description', property: 'og:description', content: desc },
    { hid: 'og:image', property: 'og:image', content: img },
    { hid: 'og:url', property: 'og:url', content: ogUrl },
    { hid: 'og:locale', property: 'og:locale', content: ogLocale },
    { hid: 'twitter:card', name: 'twitter:card', content: twitterCard },
    { hid: 'twitter:title', name: 'twitter:title', content: pageTitle },
    { hid: 'twitter:description', name: 'twitter:description', content: desc },
    { hid: 'twitter:image', name: 'twitter:image', content: img },
  ]

  const link = [
    { hid: 'canonical', rel: 'canonical', href: canonical },
    {
      hid: 'icon',
      rel: 'icon',
      type: 'image/x-icon',
      href: vm.$store.state.settings.noImage,
    },
  ]

  return { title: pageTitle, meta, link }
}

export function buildAnalyticsMeta(vm) {
  return vm.$store.state.settings.analyticSettings
    .filter((as) => as.key !== 'script')
    .map((asettings) => ({
      hid: asettings.key,
      name: asettings.key,
      content: asettings.value,
    }))
}

export function buildHeadScripts(vm) {
  return [
    ...vm.$store.state.settings.analyticSettings
      .filter((as) => as.key === 'script' && as.source === null)
      .map((asettings) => ({
        type: 'text/javascript',
        innerHTML: asettings.value,
      })),
    ...vm.$store.state.settings.analyticSettings
      .filter((as) => as.key === 'script' && as.source !== null)
      .map((asettings) => ({
        type: 'text/javascript',
        src: asettings.source,
        innerHTML: asettings.value,
      })),
    ...vm.$store.state.settings.contents.customScripts
      .filter((s) => s?.overview !== '-' && s?.overview !== '')
      .map((script) => ({
        type: 'text/javascript',
        innerHTML: script?.overview,
      })),
    ...vm.$store.state.settings.contents.customScripts
      .filter((s) => s?.description !== '-' && s?.description !== '')
      .map((script) => ({
        type: 'text/javascript',
        src: script?.description,
        async: true,
        defer: true,
      })),
    ...vm.$store.state.settings.contents.googleAnalytics
      .filter((ga) => ga?.title.startsWith('G-'))
      .map((ga) => ({
        type: 'text/javascript',
        src: `https://www.googletagmanager.com/gtag/js?id=${ga?.title}`,
        async: true,
        defer: true,
      })),
    ...vm.$store.state.settings.contents.googleAnalytics
      .filter((ga) => ga?.title.startsWith('G-') && ga?.title !== '')
      .map((ga) => ({
        type: 'text/javascript',
        innerHTML: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${ga?.title}');`,
      })),
    ...(vm.$store.state.settings.customScripts || [])
      .filter((s) => s?.overview !== '-' && s?.overview !== '')
      .map((script) => ({
        type: 'text/javascript',
        innerHTML: script?.overview,
      })),
    ...(vm.$store.state.settings.customScripts || [])
      .filter((ga) => ga?.description !== '-' && ga?.description !== '')
      .map((ga) => ({
        type: 'text/javascript',
        src: ga?.description,
        async: true,
        defer: true,
      })),
  ]
}
